import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { RankingStatusEnum } from '@flipster/apis/v1/tradingCompetition/types';
import { FormattedMessage } from '@flipster/i18n';
import { Datetime } from '@flipster/react/design-utils/datetime';
import { OrdinalNumber } from '@flipster/react/design-utils/ordinal-number';
import { css, cx } from '@flipster/styled-system/css';
import { center, hstack, stack } from '@flipster/styled-system/patterns';
import { Button } from '@flipster/ui/Button';
import { Price } from '@flipster/ui/Price';
import firstCrown from '@flipster/ui/assets/images/competition/first-crown.png';
import secondCrown from '@flipster/ui/assets/images/competition/second-crown.png';
import thirdCrown from '@flipster/ui/assets/images/competition/third-crown.png';
import ChevronRightIcon from '@flipster/ui/assets/svgs/chevron-right.svg';
import { UsdPrecision, UsdtCurrency } from '@flipster/utils/constants/currency';
import { DateFormatter } from '@flipster/utils/datetime';
import webviewHandler from '@flipster/utils/webview-handler';

import { useAuth } from '../../../shared/auth/state';
import { competitionBunja, competitionRankingBunja } from '../../bunja';

const pageSize = 25;

const highRankingImageMapper = [firstCrown, secondCrown, thirdCrown];

export const RankingList = () => {
  const { user } = useAuth();
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(1);
  const { competitionAtom } = useBunja(competitionBunja);
  const { rankingsAtom, lastUpdatedAtAtom } = useBunja(competitionRankingBunja);
  const competition = useAtomValue(competitionAtom);
  const rankings = useAtomValue(rankingsAtom);
  const lastUpdatedAt = useAtomValue(lastUpdatedAtAtom);
  const isPnlCompetition = competition?.rankBy === 'PNL';
  const isShowMore = currentPage * pageSize < rankings.length;
  return (
    <div className={css({ mb: { base: 36, tablet: 70 } })}>
      <div className={hstack({ justify: 'space-between', mb: { base: 18, tablet: 24 } })}>
        <h2 className={css({ textStyle: 'Title/M' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Ranking' />
        </h2>
        <span className={css({ textStyle: 'Text/S/Regular', color: '#DDD' /* Gray D */ })}>
          <span>
            <FormattedMessage id='Autogen_common.r251126.s_Latest_update' />
          </span>
          <Datetime
            value={lastUpdatedAt}
            format={DateFormatter.ShortYearDateTime}
            isUTC
            withUTCText
            css={{
              textStyle: 'Legacy/Number2',
            }}
          />
        </span>
      </div>
      <ul className={css({ color: 'content.default.level-5' })}>
        {rankings.slice(0, currentPage * pageSize).map((userRanking, i) => {
          const rankingImage = highRankingImageMapper[i];
          return (
            <li
              className={css(
                {
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pos: 'relative',
                  gap: 16,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  bgColor: 'background.default.level-3',
                  rounded: 8,
                  _hover: {
                    _after: {
                      pos: 'absolute',
                      top: 0,
                      left: 0,
                      w: '100%',
                      h: '100%',
                      content: "''",
                      bgColor: 'inverse.white/10',
                      rounded: 8,
                    },
                  },
                  tabletDown: {
                    h: 48,
                    p: '0 16px 0 84px',
                  },
                  tablet: {
                    h: 60,
                    p: '0 100px 0 264px',
                  },
                  '& + &': {
                    mt: {
                      base: 8,
                      tablet: 16,
                    },
                  },
                  '&:nth-child(-n + 3)': {
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: '#23539e', // legacy BLUE_DARK. should be changed
                    '& .rank': {
                      textStyle: 'Text/L/Strong',
                    },
                  },
                },
                user?.userId === userRanking.userId && {
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'background.accent.primary',
                },
              )}
              key={userRanking.nickname}
              onClick={() =>
                webviewHandler.postMessage(
                  {
                    action: 'link',
                    params: {
                      page: `user/profile/${userRanking.userProfileId}`,
                    },
                  },
                  () => router.push(`/user/profile/${userRanking.userProfileId}`),
                )
              }
            >
              {userRanking.rank <= 3 && (
                <div
                  className={center({
                    pos: 'absolute',
                    top: 0,
                    bottom: 0,
                    bgImage: 'linear-gradient(112deg, #6ca2e8 9.55%, {colors.background.accent.primary} 91.43%)', // legacy BLUE_LIGHT. should be changed
                    transform: 'skewX(145deg)',
                    tabletDown: { left: -20, w: 80 },
                    tablet: { left: -24, w: 156 },
                  })}
                />
              )}
              <div
                className={cx(
                  'rank',
                  center({
                    pos: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    w: { base: 60, tablet: 120 },
                    textStyle: 'Text/L/Regular',
                  }),
                )}
              >
                <OrdinalNumber value={userRanking.rank} max={3} />
              </div>
              {rankingImage && (
                <Image
                  src={rankingImage}
                  alt='high-ranking'
                  className={css({
                    pos: 'absolute',
                    tabletDown: {
                      left: 50,
                      w: 24,
                      h: 15,
                      filter: 'drop-shadow(-4px 2px 1px rgba({colors.gray.black/40}))',
                    },
                    tablet: {
                      left: 110,
                      w: 44,
                      h: 28,
                      filter: 'drop-shadow(-12px 2px 2px rgba({colors.gray.black/40}))',
                    },
                  })}
                />
              )}
              <p className={css({ textStyle: 'Text/L/Regular' })}>{userRanking.nickname}</p>
              <div className={stack({ textAlign: 'right' })}>
                <Price
                  price={isPnlCompetition ? userRanking.pnl : userRanking.volume}
                  currency={UsdtCurrency}
                  precision={UsdPrecision}
                  withPlus={isPnlCompetition}
                  withColor
                  fontType='num2'
                />
                {userRanking.rankingStatus === RankingStatusEnum.Unfulfilled && (
                  <p className={css({ textStyle: 'Text/S/Regular', color: 'content.interaction.disabled' })}>
                    <FormattedMessage id='Autogen_common.r251126.s_Unfulfilled' />
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {isShowMore && (
        <div className={center({ pt: 30 })}>
          <Button
            shape='trans'
            className={css({
              p: 2,
              color: 'content.default.level-5',
              '& svg': {
                transform: 'rotate(90deg)',
                tabletDown: { w: 18, h: 18 },
                tablet: { w: 24, h: 24 },
              },
            })}
            onClick={() => setCurrentPage((page) => page + 1)}
          >
            <ChevronRightIcon />
          </Button>
        </div>
      )}
    </div>
  );
};
